import c from 'classnames';
import { DynamicIcon } from 'phosphor-icons/react';

export type LabelAppearance = 'editorial' | 'highlight' | 'item' | 'alert';

type Props = {
  appearance: LabelAppearance;
  isDarkTheme?: boolean;
  iconName?: string;
  text: string;
  className?: string;
  testId?: string;
};

const styleClasses = {
  base: 'caption flex items-center',
  editorial: 'text-content-muted text-xs',
  highlight: 'py-2 px-4 rounded-lg text-sm',
  item: 'py-2 px-4 rounded-r-lg bg-black/50 text-white',
  // We are adding custom padding, leading and tracking for the alert label here because it's not part of the design system yet.
  alert:
    'px-[7.5px] leading-[20px] tracking-[-0.024px] font-normal rounded border border-danger bg-danger-surface text-danger text-xs capitalize w-fit',
};

const highlightThemeClasses = (isDarkTheme: boolean) =>
  isDarkTheme
    ? 'bg-grey-200 text-grey-900'
    : 'bg-grey-700 text-grey-100 dark:bg-surface-inverse dark:text-content-inverse';

export const Label = ({
  text,
  iconName,
  appearance,
  isDarkTheme = false,
  className = '',
  testId = '',
}: Props): React.JSX.Element => {
  return (
    <div data-test={testId}>
      <div
        className={c(
          styleClasses.base,
          styleClasses[appearance],
          appearance === 'highlight' && highlightThemeClasses(isDarkTheme),
          className
        )}
      >
        {iconName && (
          <DynamicIcon
            aria-hidden="true"
            iconName={iconName}
            className={c('h-[16px] w-[16px] lg:h-[20px] lg:w-[20px]', {
              'mr-1': appearance === 'editorial',
              'mr-2': appearance !== 'editorial',
            })}
          />
        )}
        <span>{text}</span>
      </div>
    </div>
  );
};
